import { useCallback, useEffect, useMemo } from "react";
import { usePolledDate } from "@sablier/v2-hooks";
import { BigNumber, _ } from "@sablier/v2-mixins";
import { dayjs } from "@sablier/v2-mixins";
import {
  useDurationAccessor,
  useDurationField,
  useDurationForm,
  useModalDurationPicker,
} from "~/client/hooks";

export function useInit(milliseconds?: string) {
  const update = useDurationForm((state) => state.api.updateFields);

  useEffect(() => {
    if (milliseconds) {
      const duration = _.toDuration(milliseconds, "time-short");

      if (duration[1].length) {
        const yearItem = duration[1].find((i) => i[1] === "year");
        const dayItem = duration[1].find((i) => i[1] === "day");
        const hourItem = duration[1].find((i) => i[1] === "hour");

        update({
          years: {
            value: yearItem ? _.toString(yearItem[0]) : undefined,
          },
          days: {
            value: dayItem ? _.toString(dayItem[0]) : undefined,
          },
          hours: {
            value: hourItem ? _.toString(hourItem[0]) : undefined,
          },
        });
      }
    }
  }, [milliseconds, update]);
}

export function useFieldYears() {
  const years = useDurationField("years");
  const access = useDurationAccessor();

  const onChange = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      update({ years: { value } });
    },
    [access],
  );

  return { years, onChange };
}

export function useFieldDays() {
  const days = useDurationField("days");
  const access = useDurationAccessor();

  const onChange = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      update({ days: { value } });
    },
    [access],
  );

  return { days, onChange };
}

export function useFieldHours() {
  const hours = useDurationField("hours");
  const access = useDurationAccessor();

  const onChange = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      update({ hours: { value } });
    },
    [access],
  );

  return { hours, onChange };
}

function compute<T extends { value?: string }>(years: T, days: T, hours: T) {
  /**
   * There used to be an error with durations that take into account DST. Switching to UTC time fixes it.
   * E.g. user selects 30 days, the diff here approximates 30 days and 1 hour.
   *
   * https://day.js.org/docs/en/manipulate/utc-offset
   * https://day.js.org/docs/en/parse/utc
   */
  const now = dayjs.utc();
  const y = new BigNumber(
    now.add(_.toNumber(years.value) || 0, "year").diff(now),
  ).dividedBy(1000);
  const d = new BigNumber(days.value || 0).times(24 * 60 * 60);
  const h = new BigNumber(hours.value || 0).times(60 * 60);

  return y.plus(d).plus(h).times(1000).toNumber();
}

export function useFormPrepare() {
  const { data, doClose } = useModalDurationPicker();
  const access = useDurationAccessor();

  const years = useDurationField("years");
  const days = useDurationField("days");
  const hours = useDurationField("hours");

  const total = useMemo(
    () => compute(years.field, days.field, hours.field),
    [days, hours, years],
  );

  const polled = usePolledDate({ difference: total });
  const estimated = useMemo(
    () => _.toDuration(_.toString(polled), "date-estimate")[0],
    [polled],
  );

  const onConfirm = useCallback(() => {
    const state = access();
    const { years, days, hours } = state.fields;
    const total = compute(years, days, hours);

    if (_.isFunction(data.onConfirm)) {
      const result = total ? _.toString(total) : undefined;

      data.onConfirm(result);
    }
    doClose();
  }, [access, data, doClose]);

  const onClear = useCallback(() => {
    if (_.isFunction(data.onConfirm)) {
      data.onConfirm("");
    }
    doClose();
  }, [data, doClose]);

  return {
    estimated,
    onClear,
    onConfirm,
  };
}
